import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase.config';
import './SearchResult.css';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const SearchResult = () => {
  const { searchQuery } = useParams(); // Extract the search query from the URL
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({}); // Properly initialized state for quantities
  const [maxQuantities, setMaxQuantities] = useState({}); // Properly initialized state for maxQuantities
  const [cartItems, setCartItems] = useState([]); // Properly initialized state for cartItems

  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        const productsRef = collection(db, 'Products');
        const querySnapshot = await getDocs(productsRef);

        const filteredProducts = querySnapshot.docs
          .map((doc) => {
            const product = doc.data();
            const titleLower = product.title.toLowerCase();
            const descriptionLower = product.description.toLowerCase();
            const queryLower = searchQuery.toLowerCase();

            const isMatch =
              titleLower.includes(queryLower) ||
              descriptionLower.includes(queryLower);

            if (isMatch) {
              return { id: doc.id, ...product };
            }
            return null;
          })
          .filter((product) => product !== null);

        // Initialize quantities for the search results
        const initialQuantities = filteredProducts.reduce((acc, product) => {
          acc[product.id] = 1; // Set default quantity to 1 for each product
          return acc;
        }, {});
        setQuantities(initialQuantities);

        setSearchResults(filteredProducts);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching search results:', error);
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [searchQuery]);

  const handleIncrement = (id) => {
    setQuantities((prev) => {
      const currentQuantity = prev[id] || 1;
      const maxQuantity = maxQuantities[id] || Infinity;
      return {
        ...prev,
        [id]: Math.min(currentQuantity + 1, maxQuantity),
      };
    });
  };

  const handleDecrement = (id) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: Math.max((prev[id] || 1) - 1, 1),
    }));
  };

  const handleAddToCart = async (product) => {
    const cartItemsFromStorage =
      JSON.parse(localStorage.getItem('cartItems')) || [];

    try {
      const productRef = doc(db, 'Products', product.id);
      const productDoc = await getDoc(productRef);

      const maxQuantity = productDoc.exists()
        ? productDoc.data().quantity
        : 0;

      setMaxQuantities((prev) => ({
        ...prev,
        [product.id]: maxQuantity,
      }));

      const itemToAdd = {
        ...product,
        quantity: quantities[product.id] || 1,
        maxQuantity,
      };

      const existingItem = cartItemsFromStorage.find(
        (item) => item.id === product.id
      );

      if (!existingItem) {
        const updatedCartItems = [...cartItemsFromStorage, itemToAdd];
        setCartItems(updatedCartItems);
        localStorage.setItem('cartItems', JSON.stringify(updatedCartItems));
        toast.success('Product added to shopping cart');
      } else {
        toast.info('Product is already in the cart');
      }
    } catch (error) {
      console.error('Error fetching product document:', error);
      toast.error('Failed to add product to cart');
    }
  };

  if (loading) {
    return <p>Loading results...</p>;
  }

  return (
    <>
      <Helmet>
        <title>Search Results for "{searchQuery}" - [electrozone]</title>
        <meta
          name="description"
          content={`Browse through our search results for "${searchQuery}" and find the best deals on products.`}
        />
        <meta
          property="og:title"
          content={`Search Results for "${searchQuery}" - [electrozone]`}
        />
        <meta
          property="og:description"
          content={`Browse through our search results for "${searchQuery}" and find the best deals on products.`}
        />
        <meta property="og:image" content="URL of an image representing your store" />
        <meta
          property="og:url"
          content={`https://www.yoursite.com/search/${searchQuery}`}
        />
        <link
          rel="canonical"
          href={`https://www.yoursite.com/search/${searchQuery}`}
        />
      </Helmet>

      <div className="search-results-container">
        <h1>Search Results for "{searchQuery}"</h1>
        {searchResults.length === 0 ? (
          <p>No products found matching "{searchQuery}"</p>
        ) : (
          <ul className="search-results-list">
            {searchResults.map((product) => (
              <li key={product.id} className="product-item">
                <div className="product-image-container">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.title}
                      className="product-image"
                    />
                  ) : (
                    <p>No image available</p>
                  )}
                </div>
                <div className="product-info">
                  <h2>{product.title}</h2>
                  <p>{product.description}</p>
                  <p className="product-price">Price: {product.price} DT</p>
                
                <div className="quantity-control">
                  <button
                    className="quantity-button"
                    onClick={() => handleDecrement(product.id)}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantities[product.id] || 1} // Ensure quantities are properly accessed
                    readOnly
                    className="quantity-input"
                  />
                  <button
                    className="quantity-button"
                    onClick={() => handleIncrement(product.id)}
                  >
                    +
                  </button>
                </div>
                <button
                  className="add-to-cart-btn"
                  onClick={() => handleAddToCart(product)}
                >
                  Add to Cart
                </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

export default SearchResult;
